import { Button, Table, Tabs, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import type { OfficerDto, OfficerSortField, OrgUnitDto, Paginated } from '@pams/shared';
import { OFFICER_SORT_FIELDS } from '@pams/shared';
import { Attribute, CODE_CHIP } from '@/components/Attribute';
import { UnitElevationControl } from '@/components/elevation/UnitElevation';
import { EmptyState } from '@/components/EmptyState';
import { StatusDot } from '@/components/StatusDot';
import { StatusPill } from '@/components/StatusPill';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import type { OrgUnitCounts } from '@/hooks/useOrgUnitCounts';
import { READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import { ORG_UNIT_SUBTYPE_LABEL, ORG_UNIT_TYPE_LABEL } from '@/lib/labels';
import { api } from '@/lib/api';
import { sortTerritorialFirst } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';

/**
 * The selected unit's detail, inside the Organisation drawer.
 *
 * Left: identity and attributes, as stacked cards — code, name, type, parent, status,
 * counts. Right: the substance — child units, the officers posted here, and the unit's
 * place in the hierarchy.
 *
 * The officers list is `useServerTable`'s THIRD consumer, and the first thing in the
 * codebase that actually needs `urlKeyPrefix` (a second table on a page that already has
 * one) and `enabled` (no selection yet means no query).
 */
export type OrgUnitDetailProps = {
  unit: OrgUnitDto;
  /** Absent when the counts payload has no row for this unit — never faked as zero. */
  counts: OrgUnitCounts | undefined;
  countsLoading: boolean;
  /** Every unit in the payload, for the parent lookup and the child list. */
  allUnits: readonly OrgUnitDto[];
  canWrite: boolean;
  /** [C5c §11c] Set when this is the node an elevated caller acts at: visible, never changed here. */
  editRefusal?: string;
  /** [C5c §11h] `org.elevate` — SUPER_ADMIN. Everyone else reads the unit's nodal access level. */
  canElevate: boolean;
  canReparent: boolean;
  onAddChild: () => void;
  onEdit: () => void;
  onMove: () => void;
  onSelectUnit: (unit: OrgUnitDto) => void;
};

export const OrgUnitDetail = ({
  unit,
  counts,
  countsLoading,
  allUnits,
  canWrite,
  editRefusal,
  canElevate,
  canReparent,
  onAddChild,
  onEdit,
  onMove,
  onSelectUnit,
}: OrgUnitDetailProps) => {
  const parent = unit.parentId ? allUnits.find((candidate) => candidate.id === unit.parentId) : undefined;
  /*
   * ⚠️ [amended WINGS.1] Territorial first, for the same reason the tree beside it is:
   * when the selected node is the commissionerate this list IS the thirty-six divisions,
   * so an unordered one would disagree with the tree on the same screen.
   */
  const children = sortTerritorialFirst(allUnits.filter((candidate) => candidate.parentId === unit.id));

  const officers = useServerTable<OfficerDto, OfficerSortField>({
    sortFields: OFFICER_SORT_FIELDS,
    defaultSortBy: 'name',
    defaultLimit: 10,
    // The two-tables-one-page case, at last: without this the detail table's `page` would
    // be the same URL key the (future) flat table writes.
    urlKeyPrefix: 'posted',
    queryKey: (params) => queryKeys.officers.list({ ...params, orgUnitId: unit.id, includeDescendants: false }),
    queryFn: (params, signal) =>
      api.get<Paginated<OfficerDto>>('/officers', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          orgUnitId: unit.id,
          includeDescendants: false,
        },
        signal,
      }),
    enabled: Boolean(unit.id),
  });

  const allOfficerColumns: TableProps<OfficerDto>['columns'] = [
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: true },
    { title: 'Rank', key: 'rank', render: (_value, row) => row.rank?.shortName ?? '—' },
    {
      title: 'Status',
      key: 'isActive',
      render: (_value, row) =>
        row.isActive ? <StatusDot tone="success" label="Active" /> : <StatusDot tone="neutral" label="Inactive" />,
    },
  ];

  /*
   * ⚠️ QUALIFIED, because this panel renders INSIDE `/org-tree`, which has its own Columns
   * control. Two triggers named "Columns" on one screen is a duplicate accessible name — the
   * failure the repo already legislates against for `Edit` and `Transfer`.
   */
  const officerColumnKeys = columnKeySet(allOfficerColumns ?? []);
  const officerColumnVisibility = useColumnVisibility({
    ...officerColumnKeys,
    urlKey: 'postedHide',
    storageKey: 'org-unit-officers',
  });
  const officerColumns = (allOfficerColumns ?? []).filter((column) =>
    officerColumnVisibility.isVisible(String(column.key)),
  );

  return (
    <div className="flex flex-col gap-4">
      {/*
        ⚠️ [P4-UX] ONLY THESE TWO CARDS PAIR UP. Identity and Establishment are both short
        `<dl>`s of label/value rows, so side by side they read as one header block and stop
        the drawer opening on two half-empty columns. Everything below them — the actions,
        and the officers/children/lineage tabs — is FULL WIDTH, because a table and a
        lineage tree are the substance and get the whole measure.

        A CONTAINER query, not `md:`: this renders inside a Drawer portalled to <body>, so a
        window breakpoint would describe the viewport rather than the 920px panel it is
        actually in. `OrgTree/index.tsx` puts the `@container` on the drawer's wrapper for
        exactly this. 640px is where two ~300px columns of label/value rows stay legible;
        below it they stack.
      */}
      <div className="grid grid-cols-1 items-start gap-4 @min-[640px]:grid-cols-2">
        <Card>
          <CardHeader className="gap-2 p-4">
            <div className="flex items-center gap-2">
              <span className={CODE_CHIP}>{unit.shortCode}</span>
              {/* ONE pill, on the record's primary state. The list uses dots. */}
              {unit.isActive ? (
                <StatusPill tone="success">Active</StatusPill>
              ) : (
                <StatusPill tone="neutral">Inactive</StatusPill>
              )}
            </div>
            <CardTitle className="text-base">{unit.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <dl className="divide-y divide-border">
              <Attribute label="Type">{ORG_UNIT_TYPE_LABEL[unit.type]}</Attribute>
              <Attribute label="Sub-type">{ORG_UNIT_SUBTYPE_LABEL[unit.subType]}</Attribute>
              <Attribute label="Parent">
                {parent ? (
                  <button type="button" className="underline underline-offset-4" onClick={() => onSelectUnit(parent)}>
                    {parent.name}
                  </button>
                ) : (
                  <span className="text-subtle">Root of your scope</span>
                )}
              </Attribute>
              <Attribute label="Depth">{unit.depth}</Attribute>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4">
            <CardTitle className="text-sm">Establishment</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <dl className="divide-y divide-border">
              <Attribute label="Officers here">{counts?.officersOwn ?? (countsLoading ? '…' : '—')}</Attribute>
              {/*
               * [amended P1b] Read from the SAME counts payload as the tree column.
               * It used to come from a separate `/officers?includeDescendants=true&limit=1`
               * probe keyed under `orgUnits.detail`, which meant two independently
               * cached sources for one number — and two numbers that could visibly
               * disagree on the same screen.
               */}
              <Attribute label="Officers in subtree">
                {counts?.officersSubtree ?? (countsLoading ? '…' : '—')}
              </Attribute>
              <Attribute label="Nodals here">{counts?.nodalsOwn ?? (countsLoading ? '…' : '—')}</Attribute>
              <Attribute label="Child units">{children.length}</Attribute>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* ⚠️ [C5c §11h, amended] THE ONE PLACE A UNIT'S NODAL ACCESS LEVEL IS SET — it is the unit's
          setting, and applies to every nodal officer posted here. Full width, below the two cards. */}
      <UnitElevationControl unit={unit} allUnits={allUnits} canSet={canElevate} />

      <div className="flex flex-wrap gap-2">
        <Tooltip title={canWrite ? editRefusal : READ_ONLY_TOOLTIP}>
          <Button disabled={!canWrite || editRefusal !== undefined} onClick={onEdit}>
            Edit unit
          </Button>
        </Tooltip>
        <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
          <Button disabled={!canWrite} onClick={onAddChild}>
            Add child unit
          </Button>
        </Tooltip>
        {/*
              `org.reparent` is SUPER_ADMIN only, and this affordance is ABSENT rather
              than disabled: a greyed-out "Move" is not something a nodal can interpret,
              and unlike Edit it is not an action their role merely lacks today.
            */}
        {canReparent && <Button onClick={onMove}>Move…</Button>}
      </div>

      <Tabs
        items={[
          {
            key: 'officers',
            label: `Officers posted here (${counts?.officersOwn ?? '…'})`,
            children: (
              <>
                <div className="mb-2 flex justify-end">
                  <ColumnsMenu
                    columns={allOfficerColumns ?? []}
                    visibility={officerColumnVisibility}
                    locked={officerColumnKeys.locked}
                    qualifier="officers posted here"
                    testId="columns-menu-posted"
                  />
                </div>
                <Table<OfficerDto>
                  {...officers.tableProps}
                  columns={officerColumns}
                  size="small"
                  locale={{ emptyText: <EmptyState size="sm" title="No officers are posted to this unit." /> }}
                />
              </>
            ),
          },
          {
            key: 'children',
            label: `Child units (${children.length})`,
            children:
              children.length === 0 ? (
                <EmptyState size="sm" title="This unit has no child units." />
              ) : (
                <ul className="divide-y divide-border">
                  {children.map((child) => (
                    <li key={child.id}>
                      <button
                        type="button"
                        onClick={() => onSelectUnit(child)}
                        className="flex w-full items-center gap-2 py-2 text-left text-sm transition-colors hover:text-foreground"
                      >
                        <span className={CODE_CHIP}>{child.shortCode}</span>
                        <span className="truncate">{child.name}</span>
                        {!child.isActive && <StatusDot tone="neutral" label="Inactive" className="ml-auto" />}
                      </button>
                    </li>
                  ))}
                </ul>
              ),
          },
          {
            key: 'lineage',
            label: 'Lineage',
            children: (
              <ol className="space-y-1 text-sm">
                {unit.ancestors.map((ancestorId, index) => {
                  const ancestor = allUnits.find((candidate) => candidate.id === ancestorId);
                  return (
                    <li key={ancestorId} style={{ paddingLeft: index * 16 }} className="text-muted-foreground">
                      {ancestor ? `${ancestor.name} (${ancestor.shortCode})` : 'Outside your scope'}
                    </li>
                  );
                })}
                <li style={{ paddingLeft: unit.ancestors.length * 16 }} className="font-medium">
                  {unit.name} ({unit.shortCode})
                </li>
              </ol>
            ),
          },
        ]}
      />
    </div>
  );
};
